import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  postSummaryToEva,
  type InspectionSummary,
  type PostToEvaResult,
} from '../lib/notepad';

interface NavState {
  summary?: InspectionSummary;
  editedDraft?: string;
}

function snippet(text: string | null | undefined, max = 240): string {
  if (!text) return '(empty)';
  return text.length > max ? text.slice(0, max - 1) + '…' : text;
}

export function PostToEVA() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as NavState) ?? {};
  const summary = state.summary;
  const editedDraft = state.editedDraft ?? summary?.edited_draft ?? summary?.ai_draft ?? '';

  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState<PostToEvaResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!summary) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <Link to={`/job/${jobId}/summary`} className="text-sm text-gray-600 underline">
          ‹ Summary
        </Link>
        <div className="mt-3 rounded border border-gray-200 p-4 text-sm text-gray-600">
          No draft loaded. Head back and generate one.
        </div>
      </div>
    );
  }

  async function post() {
    if (posting || !summary) return;
    setPosting(true);
    setErr(null);
    try {
      const r = await postSummaryToEva(summary.id, editedDraft);
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setPosting(false);
    }
  }

  if (result) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <h1 className="text-xl font-bold">Posted to EVA</h1>
        <div className="mt-3 rounded-lg border border-eva-teal/50 bg-eva-teal/10 p-4 text-sm">
          <div className="text-eva-teal">
            ✓ Both notes posted{' '}
            {new Date(result.posted_to_eva_at).toLocaleString()}.
          </div>
          <div className="mt-2 text-xs text-gray-700">
            Draft note id: <span className="font-mono">{result.draft_note_id}</span>
            <br />
            Raw note id: <span className="font-mono">{result.raw_note_id}</span>
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button
            onClick={() => navigate(`/job/${jobId}`)}
            className="flex-1 rounded-lg bg-eva-teal py-3 text-sm font-semibold text-white"
          >
            Back to job
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link
        to={`/job/${jobId}/summary`}
        className="text-sm text-gray-600 underline"
      >
        ‹ Summary
      </Link>
      <h1 className="mt-2 text-xl font-bold">Post to EVA</h1>
      <p className="mt-1 text-sm text-gray-600">
        Both notes below will be posted to the claim file. Collaborators in EVA web can
        see them.
      </p>

      <div className="mt-4 space-y-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
            Note 1 · Field Inspection Summary
          </div>
          <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
            {snippet(editedDraft)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
            Note 2 · Field Inspection — Raw Notes
          </div>
          <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
            {snippet(summary.raw_notes_snapshot)}
          </div>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <button
          onClick={() => navigate(`/job/${jobId}/summary`)}
          disabled={posting}
          className="flex-1 rounded-lg border border-gray-300 bg-white py-3 text-sm font-semibold disabled:opacity-40"
        >
          Edit draft
        </button>
        <button
          onClick={() => void post()}
          disabled={posting || !editedDraft.trim()}
          className="flex-1 rounded-lg bg-eva-teal py-3 text-sm font-semibold text-white disabled:opacity-40"
        >
          {posting ? 'Posting…' : 'Post Both to EVA'}
        </button>
      </div>
    </div>
  );
}
